import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import type SortOrder from '@/constants/SortOrder';
import PageSizes from '@/constants/ui/PageSizes';
import type Nullable from '@/types/helpers/Nullable';
import CommonParams from '../constants/CommonParams';
import type FilterParams from '../types/FilterParams';
import type QueryProviderQuery from '../types/QueryProviderQuery';
import type SortableFields from '../types/SortableFields';
import getQuery from '../utils/getQuery';
import getSearchString from '../utils/getSearchString';

export type QueryProviderProps = {
  children: React.ReactNode;
  defaultPageSize?: number;
  filterParams: FilterParams;
  filterPrefix?: string;
  isPaginated?: boolean;
  paginationPrefix?: string;
  sortableFields?: SortableFields;
  sortingPrefix?: string;
};

type QueryProviderContext<FiltersType = Record<string, unknown>> = {
  clearQuery: () => void;
  filters: FiltersType;
  filterParams: FilterParams;
  pagination: Partial<{ page: number; pageSize: number }>;
  query: QueryProviderQuery<FiltersType>;
  sortableFields: SortableFields;
  sorting: Partial<{ sortBy: string; sortOrder: SortOrder }>;
  updateQuery: (
    update: Partial<
      Record<keyof QueryProviderQuery<FiltersType>, Nullable<unknown>>
    >,
  ) => void;
};

const QueryContext = React.createContext<
  QueryProviderContext<unknown> | undefined
>(undefined);

export function useQueryProvider<FiltersType = Record<string, unknown>>() {
  const context = React.useContext(QueryContext);
  if (!context) {
    throw new Error('useQueryProvider must be within a QueryProvider');
  }
  return context as QueryProviderContext<FiltersType>;
}

function QueryProvider<FiltersType = Record<string, unknown>>({
  children,
  defaultPageSize = PageSizes[1],
  filterParams,
  filterPrefix,
  isPaginated = false,
  paginationPrefix,
  sortableFields = {},
  sortingPrefix,
}: QueryProviderProps) {
  const history = useHistory();
  const { pathname, search: searchString } = useLocation();
  const [query, setQuery] = React.useState(
    getQuery<FiltersType>({
      defaultPageSize,
      filterParams,
      isPaginated,
      searchString,
      sortableFields,
    }),
  );

  React.useEffect(() => {
    const newQuery = getQuery<FiltersType>({
      defaultPageSize,
      filterParams,
      filterPrefix,
      isPaginated,
      paginationPrefix,
      searchString,
      sortableFields,
      sortingPrefix,
    });
    if (!isEqual(newQuery, query)) {
      setQuery(newQuery);
    }
  }, [
    defaultPageSize,
    filterParams,
    isPaginated,
    searchString,
    sortableFields,
  ]);

  // These are memoized convenience values that return subsets of the whole
  // query based on whether they pertain to filters, pagination, or sorting.
  const { filters, pagination, sorting } = React.useMemo(
    () => ({
      filters: omit(query, Object.values(CommonParams)) as FiltersType,
      pagination: pick(query, [CommonParams.PAGE, CommonParams.PAGE_SIZE]),
      sorting: pick(query, [CommonParams.SORT_BY, CommonParams.SORT_ORDER]),
    }),
    [query],
  );

  const updateQuery = React.useCallback(
    (
      update: Partial<
        Record<keyof QueryProviderQuery<FiltersType>, Nullable<unknown>>
      >,
    ) => {
      const newSearchString = getSearchString<FiltersType>({
        filterParams,
        filterPrefix,
        paginationPrefix,
        query,
        sortingPrefix,
        sortableFields,
        update,
        urlSearch: new URLSearchParams(searchString),
      });

      // If the search params have not changed, don't do anything.
      if (searchString === newSearchString) return;

      history.push({
        pathname,
        search: newSearchString,
      });
    },
    [history, pathname, query, searchString, sortableFields],
  );

  const clearQuery = React.useCallback(() => {
    const clearedQuery = Object.keys(query).reduce(
      (obj, key) => ({
        ...obj,
        [key]: null,
      }),
      {},
    ) as Record<keyof QueryProviderQuery<FiltersType>, null>;
    updateQuery(clearedQuery);
  }, [query, updateQuery]);

  const value = React.useMemo(
    () => ({
      clearQuery,
      filters,
      filterParams,
      pagination,
      query,
      sortableFields,
      sorting,
      updateQuery,
    }),
    [
      clearQuery,
      filters,
      filterParams,
      pagination,
      query,
      sortableFields,
      sorting,
      updateQuery,
    ],
  );

  const { Provider } = QueryContext as React.Context<
    QueryProviderContext<FiltersType>
  >;

  return <Provider value={value}>{children}</Provider>;
}

export default QueryProvider;
